<template>
  <div class="result-container">
    <h1>{{ $i18n.t('helpy_search_results') }}</h1>

    <el-divider>{{ $i18n.t('helpy_categories') }}</el-divider>
    <div v-if="isEmpty(categories)">
      {{ $i18n.t('matching_category_not_found') }}
    </div>

    <template v-else>
      <el-breadcrumb v-for="{ id } in categories" :key="`category-result-${id}`" separator="/">
        <el-breadcrumb-item v-for="{ title, id: crumbId } in getBreadcrumb(id)" :key="`category-bc-${id}-${crumbId}`">
          <span class="el-link fw-bold color-primary" @click.prevent="onCategorySelect(crumbId)">
            {{$i18n.t(title)}}
          </span>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <el-divider>{{ $i18n.t('helpy_contents') }}</el-divider>
    <div class="d-grid gap-1 justify-items-start">
      <div v-if="isEmpty(contents)">
        {{ $i18n.t('matching_content_not_found') }}
      </div>
      <div v-else v-for="(content, key) in contents" :key="`content-result-${key}`">
        <el-breadcrumb>
          <el-breadcrumb-item
            v-for="{ title, id: crumbId } in getBreadcrumb(content.parent)"
            :key="`content-result-bc-${crumbId}`"
          >
            <span class="el-link fw-bold color-primary" @click.prevent="onCategorySelect(crumbId)">
              {{ $i18n.t(title) }}
            </span>
          </el-breadcrumb-item>
          <el-breadcrumb-item>
            <span class="el-link fw-bold color-primary" @click="onContentSelect(content)">{{ content.title }}</span>
          </el-breadcrumb-item>
        </el-breadcrumb>
        <ul>
          <li v-for="(context, contextKey) in getContexts(content)" :key="`result-context-${key}-${contextKey}`">
            <div v-html="context" />
          </li>
        </ul>
      </div>
    </div>

    <div v-if="!isEmpty(globalResults)">
      <h1>{{ $i18n.t('helpy_global_search_results') }}</h1>

      <el-divider>{{ $i18n.t('helpy_contents') }}</el-divider>
      <div class="d-grid gap-1 justify-items-start">
        <div v-for="(content, key) in globalResults" :key="`content-result-${key}`">
          <el-breadcrumb>
            <el-breadcrumb-item
              v-for="{ title, id: crumbId } in getBreadcrumb(content.parent)"
              :key="`content-result-bc-${crumbId}`"
            >
              <span class="el-link fw-bold color-primary" @click.prevent="onCategorySelect(crumbId)">
                {{ $i18n.t(title) }}
              </span>
            </el-breadcrumb-item>
            <el-breadcrumb-item>
              <span class="el-link fw-bold color-primary" @click="onContentSelect(content)">{{ content.title }}</span>
            </el-breadcrumb-item>
          </el-breadcrumb>
          <ul>
            <li v-for="(context, contextKey) in getContexts(content)" :key="`result-context-${key}-${contextKey}`">
              <div v-html="context" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, isEmpty } from 'lodash'
import { htmlToText } from '../../utils/html-to-text'
import { highlight } from '../../utils'

export default {
  name: 'HelpySearchResults',
  props: {
    filter: {
      type: Array,
      default: () => [],
    },
    results: {
      type: Object,
      default: () => {
        return {
          categories: [],
          contents: [],
          global: [],
        }
      },
    },
  },
  computed: {
    categories() {
      return this.results.categories
    },
    contents() {
      return this.results.contents
    },
    globalResults() {
      return this.results.globalResults
    },
    index() {
      return this.$get('index')
    },
  },
  methods: {
    highlight,
    isEmpty,
    getBreadcrumb(nodeId) {
      const parentId = get(this.index, [nodeId, 'parent'], 'root')
      const parentNode = get(this.index, parentId, 'root')
      const parentTitle = get(parentNode, 'title', 'root')
      return parentTitle === 'root' ? [this.index[nodeId]] : this.getBreadcrumb(parentId).concat([this.index[nodeId]])
    },
    getContexts({ content }) {
      let contexts = []
      this.filter.forEach((tag) => {
        const regex = new RegExp(`\\n?([^\\s.]+\\s){0,20}([\\S.]+)?${tag}(\\s+)?([^\\s.]+[\\s.]){0,20}`, 'ig') // @regex match text around tag 100 chars before and after
        const matches = htmlToText(content).match(regex)
        if (matches) {
          contexts = [...contexts, ...matches]
        }
      })
      if (!contexts.length) contexts.push(htmlToText(content).substring(0, 300))
      return this.highlightContexts(contexts)
    },
    highlightContexts(contexts) {
      contexts.forEach((context, index) => {
        this.filter.forEach((tag) => {
          const highlight = this.highlight(context, tag, 'helpy-highlight')
          if (highlight.success) context = highlight.html
        })
        if (context[context.length - 1] !== '.') context += '...'
        contexts[index] = context
      })
      return contexts
    },
    onCategorySelect(id) {
      this.$emit('category-select', id)
    },
    onContentSelect(content) {
      this.$emit('content-select', {
        parent: content.parent,
        index: content.tabIndex,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.color-primary {
  color: $color-primary;
}
.result-container {
  div,
  p,
  em {
    line-height: 1.5rem;
  }
}
</style>
